import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useTeamContext } from '@/contexts/TeamContext';
import { useTeams } from '@/hooks/useTeams';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

const teamFormSchema = yup.object({
  name: yup.string().required('Team name is required'),
});

type TeamFormValues = yup.InferType<typeof teamFormSchema>;

interface TeamDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (newTeam: any) => void;
}

export const TeamAddDialog = ({
  open,
  onOpenChange,
  onSuccess,
}: TeamDialogProps) => {
  const { createTeam } = useTeams();
  const { setActiveTeam } = useTeamContext();

  const form = useForm<TeamFormValues>({
    resolver: yupResolver(teamFormSchema),
    defaultValues: {
      name: '',
    },
  });

  const onSubmit = useCallback(
    async (data: TeamFormValues) => {
      try {
        const newTeam = await createTeam.mutateAsync({
          name: data.name,
        });

        setActiveTeam(newTeam);
        onOpenChange(false);
        form.reset();
        onSuccess?.(newTeam);
      } catch (error) {
        console.error('Failed to create team:', error);
      }
    },
    [createTeam, setActiveTeam, onOpenChange, form, onSuccess],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create team</DialogTitle>
          <DialogDescription>
            Add a new team to manage projects and collaborate.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input placeholder="Team name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" isLoading={createTeam.isPending}>
                Create team
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
